import React, { useState, useCallback, useEffect } from 'react';
import { Container, Box, Snackbar, Alert, SnackbarContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Header from './components/Header';
import PromptInput from './components/PromptInput';
import AnalysisResults from './components/AnalysisResults';
import { analyzePrompt, improvePrompt } from './services/aiAnalysisService';

function App() {
  const [prompt, setPrompt] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analyzedPrompt, setAnalyzedPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isImproving, setIsImproving] = useState(false);
  const [improvedPrompt, setImprovedPrompt] = useState('');
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [isSourceMaterial, setIsSourceMaterial] = useState(false);
  const [analyzedIsSourceMaterial, setAnalyzedIsSourceMaterial] = useState(false);
  const [needsReanalysis, setNeedsReanalysis] = useState(false);


  const handleAnalyze = useCallback(async () => {
    if (!prompt.trim()) {
      // This check is important for the re-analyze flow
      setError('Prompt cannot be empty.');
      return;
    }
    setIsLoading(true);
    setError(null);
    setAnalysisResult(null);
    setImprovedPrompt(''); // Reset on new analysis
    setAnalyzedPrompt(prompt); // Lock in the prompt that is being analyzed
    setAnalyzedIsSourceMaterial(isSourceMaterial); // Lock in the toggle state at time of analysis

    try {
      const result = await analyzePrompt(prompt, isSourceMaterial);
      setAnalysisResult(result);
      setIsLoading(false);
    } catch (err) {
      const isOverloadedError = err.message.includes('overloaded');
      
      if (isOverloadedError) {
        setToast({ open: true, message: err.message, severity: 'warning' });
        // Keep button disabled for 5 seconds
        setTimeout(() => {
          setIsLoading(false);
        }, 5000); 
      } else {
        // Handle other errors
        setError(err.message || 'An unexpected error occurred. Please try again.');
        setToast({ open: true, message: err.message || 'Analysis failed!', severity: 'error' });
        setIsLoading(false);
      }
    }
  }, [prompt, isSourceMaterial]); // Dependencies for useCallback

  const handleReanalyze = (newPrompt) => {
    setPrompt(newPrompt);
    setNeedsReanalysis(true); // Set a flag to trigger analysis after state update
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // This effect waits for the 'needsReanalysis' flag to be true,
  // then calls the memoized handleAnalyze function.
  useEffect(() => {
    if (needsReanalysis) {
      handleAnalyze();
      setNeedsReanalysis(false); // Reset the flag
    }
  }, [needsReanalysis, handleAnalyze]);
  
  const handleImprovePrompt = async (answers) => {
    setIsImproving(true);
    setError(null);
    try {
      const result = await improvePrompt(analyzedPrompt, answers, analyzedIsSourceMaterial);
      setImprovedPrompt(result.improvedPrompt);
      showToast('Your prompt has been improved!', 'success');
      setIsImproving(false);
    } catch (err) {
      const isOverloadedError = err.message.includes('overloaded');
      if (isOverloadedError) {
        setToast({ open: true, message: err.message, severity: 'warning' });
        setTimeout(() => {
          setIsImproving(false);
        }, 5000);
      } else {
        const errorMessage = err.message || 'An unexpected error occurred during improvement.';
        setError(errorMessage);
        setToast({ open: true, message: errorMessage, severity: 'error' });
        setIsImproving(false);
      }
    }
  };

  const handleToastClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToast({ ...toast, open: false });
  };

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const snackbarAction = (
    <IconButton
      size="small"
      aria-label="close"
      color="inherit"
      onClick={handleToastClose}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  );

  return (
    <Box>
      <Header />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <PromptInput
          prompt={prompt}
          setPrompt={setPrompt}
          onAnalyze={handleAnalyze}
          isLoading={isLoading}
          isSourceMaterial={isSourceMaterial}
          setIsSourceMaterial={setIsSourceMaterial}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        <AnalysisResults
          isLoading={isLoading}
          result={analysisResult}
          showToast={showToast}
          onImprovePrompt={handleImprovePrompt}
          isImproving={isImproving}
          improvedPrompt={improvedPrompt}
          onReanalyze={handleReanalyze}
        />
      </Container>
      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
            // This ensures the snackbar itself doesn't exceed the width of its content
            // allowing the content to control its own max-width.
            width: 'auto',
            // Responsive positioning
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: '24px',
            '@media (max-width: 600px)': {
                width: 'calc(100% - 16px)',
                left: '8px',
                right: '8px',
                transform: 'none',
                bottom: '8px',
            },
        }}
      >
        <SnackbarContent
          message={toast.message}
          action={snackbarAction}
          sx={{
            backgroundColor: '#322F35', // MD3 Surface Container High
            color: '#D0BCFF', // MD3 Primary color for text on dark surface
            borderRadius: '4px', // 4dp
            boxShadow: '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)', // 6dp elevation
            minHeight: '48px', // For single line
            padding: '6px 16px',
            alignItems: 'center',
            flexWrap: 'nowrap', // Prevents action from wrapping on small screens
            typography: 'body2',
            fontWeight: 500,
          }}
        />
      </Snackbar>
    </Box>
  );
}

export default App;
